import { replace } from 'connected-react-router';
import { pick } from 'lodash';
import { combineReducers } from 'redux';
import { call, put, putResolve, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExternalUserDetail } from 'generated/bffUser/models';
import { SharedObjectsData } from 'generated/sharedObjects/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { PRIMARY_ADMIN_ROLE } from 'helpers/constants/accessConstants';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { EntityAccessManagementRoute } from 'helpers/routes/modules/accessManagement';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';
import { store } from 'helpers/store/configureStore';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetUserInfoSuccessType } from 'models/AuthTypes';
import {
  ClearContextEntityType,
  ClearContextRequestActionInputs,
  ClearContextSuccessActionInputs,
} from 'models/context/ClearContext';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { EntityAccessAssigned, ExternalUser } from 'models/context/ExternalUser';
import { InternalUser } from 'models/context/InternalUser';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { SetContextExternalUserRequest } from 'models/SetContextExternalUserRequest';
import { UserAccess } from 'models/UserAccess';
import { UserInfo } from 'models/UserInfo';
import { getAvailablePrivileges } from 'saga/acl/simpleUserPrivileges/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { isAppInitialized } from 'saga/appHandlers/ducks';
import { AuthActionTypes } from 'saga/auth/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { selectSharedObjectsData } from 'saga/sharedObjects/ducks';
import { canClearContextEntityCheck, clearContextAction, ContextActionTypes, selectContext } from 'saga/context/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { selectContextInternalUser } from 'saga/context/internal/ducks';

export interface ExternalUserDetailRequest {
  cpRefNo: string;
  forceCheck: boolean;
}

/* STATE */
export interface ContextExternalState {
  user: ExternalUser | null;
  userDetail: ExternalUserDetail | null;
  userAccesses: UserAccess[];
}

/* ACTION TYPES */
export enum ContextExternalActionTypes {
  SetContextExternalUser = '@@Context/External/SET_CONTEXT_EXTERNAL_USER',
  GetExternalUserDetail = '@@Context/External/GET_EXTERNAL_USER_DETAIL',
  UpdateEntityAccesses = '@@Context/External/UPDATE_ENTITY_ACCESSES',
  ValidateContextExternalUser = '@@Context/External/VALIDATE_CONTEXT_EXTERNAL_USER',
}

/* ACTIONS */
export const setContextExternalUserActions = createApiActionCreators(ContextExternalActionTypes.SetContextExternalUser);
export const getExternalUserDetailActions = createApiActionCreators(ContextExternalActionTypes.GetExternalUserDetail);
export const updateEntityAccessesActions = createApiActionCreators(ContextExternalActionTypes.UpdateEntityAccesses);
export const validateContextExternalAction = createActionCreator(
  ContextExternalActionTypes.ValidateContextExternalUser
);

/* REDUCERS */
const initialState: ContextExternalState = {
  user: null,
  userDetail: null,
  userAccesses: [],
};

const user = createReducer(initialState.user, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: ExternalUser | null, payload: ClearContextSuccessActionInputs | null) =>
      payload?.sessionContext.internal?.user &&
      canClearContextEntityCheck(payload, ClearContextEntityType.EXTERNAL_USER)
        ? null // clear external user context if internal user is logged in
        : _state,
    [RequestActionTypes.FAILURE]: (_state: ExternalUser | null) => _state,
  },
  [ContextExternalActionTypes.SetContextExternalUser]: {
    [RequestActionTypes.SUCCESS]: (
      _state: ExternalUser | null,
      payload: { data: ExternalUserDetail; isVerified?: boolean }
    ) => {
      if (payload) {
        return {
          ...pick<ExternalUserDetail>(payload.data, [
            'login',
            'displayName',
            'firstName',
            'lastName',
            'phoneNoMobile',
            'phoneNoFix',
            'email',
            'entityAccesses',
            'cpRefNo',
            'titleBefore',
            'titleAfter',
          ]),
          isVerified: payload.isVerified,
        };
      } else {
        return null;
      }
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
  [ContextExternalActionTypes.UpdateEntityAccesses]: {
    [RequestActionTypes.SUCCESS]: (state: ExternalUser, payload: { entityAccesses: EntityAccessAssigned[] }) => ({
      ...state,
      entityAccesses: payload,
    }),
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
});

const userAccesses = createReducer(initialState.userAccesses, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: UserAccess[], payload: ClearContextSuccessActionInputs) =>
      payload?.sessionContext.internal?.user &&
      canClearContextEntityCheck(payload, ClearContextEntityType.EXTERNAL_USER)
        ? [] // clear external user context if internal user is logged in
        : _state,
    [RequestActionTypes.FAILURE]: (_state: UserAccess[]) => _state,
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => [],
  },
});

const userDetail = createReducer(initialState.userDetail, {
  [ContextExternalActionTypes.GetExternalUserDetail]: {
    [RequestActionTypes.SUCCESS]: (state: ExternalUserDetail | null, payload: ExternalUserDetail) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

export default combineReducers<ContextExternalState>({
  user,
  userDetail,
  userAccesses,
});

/* SELECTORS */
const selectContextExternal = (state: AppState) => selectContext(state).external || {};

export const selectContextExternalUser = (state: AppState): ExternalUser | null => selectContextExternal(state).user;
export const selectExternalUserDetail = (state: AppState): ExternalUserDetail | null =>
  selectContextExternal(state).userDetail;

export const selectIsPrimaryAdminInContext = (state: AppState): boolean => {
  const externalUser: ExternalUser | null = selectContextExternalUser(state);
  const contextCustomer: CustomerInfo | null = selectContextCustomerInfo(state);

  return !!externalUser?.entityAccesses?.find(
    (ea) => ea.roleType === PRIMARY_ADMIN_ROLE && ea.cuRefNo === contextCustomer?.cuRefNo
  );
};

export const selectContextUserAccesses = (state: AppState): UserAccess[] => selectContextExternal(state).userAccesses;

/* SAGAS */
function* setContextExternalUser({ payload }: AppAction<SetContextExternalUserRequest>) {
  if (!payload.koId) {
    const payloadInput: ClearContextRequestActionInputs = {
      skipSharedObjectsSession: !!payload.skipSharedObjectsSession,
      clearContextEntity: ClearContextEntityType.EXTERNAL_USER,
      clearRelatedEntities: true,
    };

    yield put(clearContextAction.request(payloadInput));
    yield put(setContextExternalUserActions.success(null));
  } else {
    const appInitialized: boolean = yield select(isAppInitialized);
    const request: ApiClientRequest = {
      apiCall: call(api.getExternalUser, yield getApiUrl(environment.urlConfig.user), payload.koId, !appInitialized),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);
    const sharedObject: SharedObjectsData = yield select(selectSharedObjectsData);

    if (resp.ok) {
      yield put(
        setContextExternalUserActions.success({
          data: resp.data,
          isVerified: sharedObject?.externalUser?.verifiedUser || false,
        })
      );
    } else {
      console.log('setContextExternalUser failure: api.getExternalUser failed');
      yield put(setContextExternalUserActions.failure());
    }
  }
}

function* validateContextExternalUser() {
  // Skip validation if currentUrl target to 2-6-5
  if (window.location.pathname === EntityAccessManagementRoute.VerifyEntityAccess) {
    return;
  }

  const contextCustomer: CustomerInfo = yield select(selectContextCustomerInfo);
  const contextExternalUser: ExternalUser = yield select(selectContextExternalUser);
  const contextInternalUser: InternalUser = yield select(selectContextInternalUser);

  if (contextInternalUser && contextCustomer) {
    const availablePrivileges: Record<string, boolean> = yield call(getAvailablePrivileges, '', [
      'CONTACT_PERSON_WO_CHECK',
      'SKIP_IDENTIFICATION',
    ]);
    const SKIP_IDENTIFICATION = availablePrivileges['SKIP_IDENTIFICATION'];
    const CONTACT_PERSON_WO_CHECK = availablePrivileges['CONTACT_PERSON_WO_CHECK'];

    if (!contextExternalUser) {
      // Empty
      if (!SKIP_IDENTIFICATION) {
        // Ext. user context mandatory, go to 4.3.2 page
        const deeplinkParams: LegacyCrmDeeplinkConfig = {
          command: LegacyCrmDeeplinkCommand.IMO2DASH,
          queryParams: {
            page: 'searchContactPersonResults',
            action: 'searchContactPerson',
            logToVisitedHistory: 'false',
          },
        };

        yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
      }
    } else if (contextExternalUser?.isVerified) {
      // Verified
      return;
    } else if (
      // Unverified
      !CONTACT_PERSON_WO_CHECK &&
      contextExternalUser.cpRefNo &&
      contextExternalUser.entityAccesses?.some((access: EntityAccessAssigned) => access.entityRefNo)
    ) {
      // Ext. user verification mandatory, go to 4.3.3 page
      const deeplinkParams: LegacyCrmDeeplinkConfig = {
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: {
          page: 'contactPersonVerification',
          logToVisitedHistory: 'false',
        },
      };

      yield putResolve(redirectToLegacyCrmAction(deeplinkParams));
    }
  } else if (
    // redirect to WF 2.6.5
    contextExternalUser &&
    contextExternalUser?.entityAccesses &&
    contextExternalUser?.entityAccesses?.length > 0 &&
    contextExternalUser?.entityAccesses?.every((ea: EntityAccessAssigned) => !ea.isTrusted && !ea.isVerified)
  ) {
    store.dispatch(
      replace(
        `${buildRoute(ROUTES.VerifyEntityAccess)}?redirectUrl=${
          window.location.pathname === EntityAccessManagementRoute.VerifyEntityAccess
            ? new URLSearchParams(window.location.search).get('redirectUrl')
            : window.location.href
        }`
      )
    );

    return;
  }
}

function* getExternalUserDetail({ payload }: AppAction<ExternalUserDetailRequest>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getExternalUser,
      yield getApiUrl(environment.urlConfig.user),
      payload.cpRefNo,
      payload.forceCheck
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getExternalUserDetailActions.success(resp.data));
  } else {
    yield put(getExternalUserDetailActions.failure());
  }
}

function* afterGetUserInfoSuccess({ payload }: AppAction<GetUserInfoSuccessType>) {
  const userInfo: UserInfo = payload.userInfo;
  const forceUpdate: boolean = payload.forceUpdate;
  const externalUser: ExternalUser = yield select(selectContextExternalUser);

  if (userInfo?.logged && !userInfo?.internal && (forceUpdate || userInfo.username !== externalUser?.login)) {
    yield put(setContextExternalUserActions.request({ koId: userInfo.koId }));
  }
}

/* EXPORT */
export function* contextExternalSaga() {
  yield takeLatest(
    createActionType(ContextExternalActionTypes.SetContextExternalUser, RequestActionTypes.REQUEST),
    setContextExternalUser
  );

  yield takeLatest(createActionType(AuthActionTypes.GetUserInfo, RequestActionTypes.SUCCESS), afterGetUserInfoSuccess);
  yield takeLatest(
    createActionType(ContextExternalActionTypes.GetExternalUserDetail, RequestActionTypes.REQUEST),
    getExternalUserDetail
  );
  yield takeLatest(
    createActionType(ContextExternalActionTypes.ValidateContextExternalUser),
    validateContextExternalUser
  );
}
